import React, { useState } from 'react'
import { checkShouldRemoveOldKey } from '../channels/generic/helpers'
import SmConnectionUninstallModal from './SmConnectionUninstallModal'
import StripeUninstallModal from '../channels/stripe/StripeUninstallModal'
import {
  connectionIdForSmKeysDisconnect,
  hasSmIntegrationUninstall,
  hasStripeKeysTableDisconnect,
} from '../smUninstallEndpoints'

const disconnectBtnClassName =
  'tw-inline-flex tw-items-center tw-whitespace-nowrap tw-rounded-md tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-1 tw-text-xs tw-font-medium tw-text-slate-500 hover:tw-border-rose-200 hover:tw-bg-rose-50 hover:tw-text-rose-700 tw-transition-colors'

function keyRowAllowsDisconnect(
  ppKey: any,
  integration: { id?: string; uninstallEnabled?: boolean } | null,
  ownerId: string | number | null | undefined,
  channelId: string | undefined,
  canDisconnectAllTeamIntegrations: boolean,
  currentUserId: string | number | null | undefined
): boolean {
  if (hasStripeKeysTableDisconnect(integration)) {
    return canDisconnectAllTeamIntegrations
  }
  const cid = connectionIdForSmKeysDisconnect(ppKey, ownerId, integration?.id ?? channelId)
  if (!cid) return false
  if (canDisconnectAllTeamIntegrations) return true
  const uid = currentUserId != null && String(currentUserId).trim() !== '' ? String(currentUserId) : ''
  const keyId = ppKey?.keyId != null ? String(ppKey.keyId) : ''
  return uid !== '' && keyId.startsWith(`${uid}--`)
}

export default function ChannelKeysTable({
  ppKeys,
  integration = null,
  ownerId = null,
  canDisconnectAllTeamIntegrations = false,
  currentUserId = null,
}: {
  ppKeys: unknown[]
  integration?: { id?: string; channel?: string; title?: string; uninstallEnabled?: boolean } | null
  ownerId?: string | number | null
  canDisconnectAllTeamIntegrations?: boolean
  currentUserId?: string | number | null
}) {
  const cellPaddingClassName = 'tw-px-2 tw-py-1'
  const [uninstallConnectionId, setUninstallConnectionId] = useState<string | null>(null)
  const [stripeUninstallVisible, setStripeUninstallVisible] = useState(false)

  const { shouldRemoveOldKey, newKeys } = checkShouldRemoveOldKey(ppKeys)
  const filteredPpKeys = shouldRemoveOldKey ? newKeys : (ppKeys || [])

  const channelId = integration?.id
  const showDisconnectColumn = Boolean(
    integration &&
      (hasSmIntegrationUninstall(channelId) || hasStripeKeysTableDisconnect(integration)) &&
      filteredPpKeys.some((ppKey: any) =>
        keyRowAllowsDisconnect(ppKey, integration, ownerId, channelId, canDisconnectAllTeamIntegrations, currentUserId)
      )
  )

  const hasAccountId = filteredPpKeys.some((k: any) => k?.accountId)
  const hasPublicKey = filteredPpKeys.some((k: any) => k?.publicKey)

  const columns = [
    ...(hasAccountId ? [{ label: 'Account ID', key: 'accountId' }] : []),
    ...(hasPublicKey ? [{ label: 'Public Key', key: 'publicKey' }] : []),
    { label: 'Private Key', key: 'privateKey' },
    ...(showDisconnectColumn ? [{ label: 'Actions', key: 'actions' }] : []),
  ]

  return (
    <>
      <div className="tw-rounded-lg tw-border tw-border-slate-200 tw-w-full tw-text-sm tw-shadow-sm tw-overflow-x-auto">
        <table className="tw-table-auto tw-border-collapse tw-w-full">
          <thead className="tw-border-b tw-font-medium tw-text-slate-500 tw-bg-slate-50 tw-text-left">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={`${cellPaddingClassName}${col.key === 'actions' ? ' tw-text-center' : ''}`}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {filteredPpKeys.map((ppKey: any, i) => (
              <tr
                key={ppKey?.accountId || ppKey?.keyId || i}
                className={i !== 0 ? 'tw-border-t tw-border-slate-200' : ''}
              >
                {columns.map((col) => {
                  if (col.key === 'actions') {
                    if (hasStripeKeysTableDisconnect(integration)) {
                      return (
                        <td key={col.key} className={`${cellPaddingClassName} tw-text-center`}>
                          {i === 0 && canDisconnectAllTeamIntegrations ? (
                            <button
                              type="button"
                              className={disconnectBtnClassName}
                              onClick={() => setStripeUninstallVisible(true)}
                            >
                              Disconnect
                            </button>
                          ) : null}
                        </td>
                      )
                    }
                    const cid = connectionIdForSmKeysDisconnect(ppKey, ownerId, integration?.id)
                    const showBtn =
                      Boolean(cid) &&
                      keyRowAllowsDisconnect(
                        ppKey,
                        integration,
                        ownerId,
                        channelId,
                        canDisconnectAllTeamIntegrations,
                        currentUserId
                      )
                    return (
                      <td key={col.key} className={`${cellPaddingClassName} tw-text-center`}>
                        {showBtn ? (
                          <button
                            type="button"
                            className={disconnectBtnClassName}
                            onClick={() => setUninstallConnectionId(cid)}
                          >
                            Disconnect
                          </button>
                        ) : null}
                      </td>
                    )
                  }
                  const isPrivateKey = col.key === 'privateKey'
                  const value = ppKey?.[col.key]
                  const content = isPrivateKey ? '••••••••••' : value
                  return (
                    <td key={col.key} className={cellPaddingClassName}>
                      <span className="tw-whitespace-nowrap">{content}</span>
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {integration && hasSmIntegrationUninstall(channelId) && uninstallConnectionId ? (
        <SmConnectionUninstallModal
          visible={Boolean(uninstallConnectionId)}
          closeModal={() => setUninstallConnectionId(null)}
          channelId={integration.id}
          channelTitle={integration.channel || integration.title || integration.id}
          connectionId={uninstallConnectionId}
          ownerId={ownerId}
        />
      ) : null}

      {integration?.id === 'stripe' && stripeUninstallVisible ? (
        <StripeUninstallModal
          visible={stripeUninstallVisible}
          closeModal={() => setStripeUninstallVisible(false)}
          integration={integration}
        />
      ) : null}
    </>
  )
}
